<template>
  <ModalFormularioV2
    v-model="aberto"
    titulo="Upload de Certificado Digital A1"
    :loading="isLoading"
    tamanho="lg"
    cor-header="var(--bs-primary)"
    @confirmar="salvarCertificado"
  >
    <div class="alert alert-info border-0 shadow-sm small py-2 mb-4 d-flex align-items-start">
      <i class="bi bi-info-circle-fill me-2 mt-1"></i>
      <div>
        A Receita Federal exige o Certificado Digital A1 (e-CNPJ) para comunicação com o Portal Único Siscomex.<br>
        <strong>A sua senha será fortemente criptografada com algoritmo AES-256 no nosso servidor e nunca ficará exposta.</strong>
      </div>
    </div>

    <div class="row g-3">
      <div class="col-md-6">
        <label class="form-label is-required" for="arquivoCertificado">
          Ficheiro do Certificado (.pfx)
        </label>
        <input
          id="arquivoCertificado"
          type="file"
          class="form-control"
          :class="{ 'is-invalid': formErroArquivo }"
          accept=".pfx,.p12"
          :disabled="isLoading"
          @change="onFileChange"
        >
        <div v-if="formErroArquivo" class="invalid-feedback">Selecione um ficheiro válido.</div>
      </div>
      <div class="col-md-6">
        <CampoInput
          id="senhaCert"
          label="Senha do Certificado"
          type="password"
          v-model="senha"
          placeholder="Digite a senha do ficheiro"
          required
          :disabled="isLoading"
        />
      </div>
    </div>
  </ModalFormularioV2>
</template>

<script setup>
import { ref, computed, nextTick } from 'vue'
import api from '@/services/api'
import ModalFormularioV2 from '@/components/base/modal/ModalFormularioV2.vue'
import CampoInput from '@/components/base/campos/CampoInput.vue'

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  idFilial: { type: Number, default: 0 }
})

const emit = defineEmits(['update:modelValue', 'salvo', 'show-notification'])

const aberto = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val)
})

const isLoading = ref(false)
const arquivo = ref(null)
const senha = ref('')
const formErroArquivo = ref(false)

const focarPrimeiroErro = async () => {
  await nextTick()
  setTimeout(() => {
    const elementoComErro = document.querySelector('.is-invalid')
    if (elementoComErro) {
      elementoComErro.focus()
      elementoComErro.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }, 50)
}

const onFileChange = (e) => {
  formErroArquivo.value = false
  const file = e.target.files[0]
  if (file) arquivo.value = file
}

const resetForm = () => {
  arquivo.value = null
  senha.value = ''
  formErroArquivo.value = false
}

const salvarCertificado = async () => {
  formErroArquivo.value = false
  let hasError = false

  if (!arquivo.value) {
    formErroArquivo.value = true
    hasError = true
  }

  if (!senha.value) {
    emit('show-notification', { message: 'O campo "Senha do Certificado" é de preenchimento obrigatório.', type: 'warning' })
    hasError = true
  }

  if (hasError) {
    emit('show-notification', { message: 'Por favor, preencha todos os campos obrigatórios destacados em vermelho.', type: 'warning' })
    await focarPrimeiroErro()
    return
  }

  if (!props.idFilial) {
    emit('show-notification', { message: 'Salve a filial antes de enviar o certificado.', type: 'error' })
    return
  }

  isLoading.value = true

  const formData = new FormData()
  formData.append('IdFilial', props.idFilial)
  formData.append('Arquivo', arquivo.value)
  formData.append('Senha', senha.value)

  try {
    const res = await api.post('/FilialCertificados/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })

    emit('show-notification', { message: res.data.message || 'Upload efetuado com sucesso!', type: 'success' })
    resetForm()
    emit('salvo')
  } catch (error) {
    const data = error.response?.data
    let msg = 'Falha ao processar certificado. Verifique a senha.'

    if (typeof data === 'string') {
      msg = data
    } else if (data?.message) {
      msg = data.message
    } else if (data?.title) {
      msg = data.title
    } else if (data?.errors) {
      msg = Object.values(data.errors).flat().join('; ')
    }

    emit('show-notification', { message: msg, type: 'error' })
  } finally {
    isLoading.value = false
  }
}
</script>
